/**
 * FinancialProfilePage — Full financial profile management (Epic 6)
 * Income, Expenses, Liabilities, Assets with sharing controls
 */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  Trash2,
  Pencil,
  X,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Landmark,
  PiggyBank,
  Shield,
  ShieldCheck,
  Lock,
} from 'lucide-react'
import AppHeader from '../components/AppHeader'
import { useAuth } from '../contexts/AuthContext'
import { getMyConnections, type Connection } from '../api/brokerApi'
import { formatNumberWithCommas, stripToNumber } from '../utils/formatCurrency'
import {
  type IncomeEntry,
  type ExpenseEntry,
  type LiabilityEntry,
  type AssetEntry,
  type IncomeType,
  type ExpenseType,
  type LiabilityType,
  type AssetType,
  type Frequency,
  getIncomes,
  getExpenses,
  getLiabilities,
  getAssets,
  createIncome,
  createExpense,
  createLiability,
  createAsset,
  updateIncome,
  updateExpense,
  updateLiability,
  updateAsset,
  deleteIncome,
  deleteExpense,
  deleteLiability,
  deleteAsset,
  shareFinancials,
  getFinancialsSharedStatus,
} from '../api/incomeExpenseApi'

// ── Constants ────────────────────────────────────────────────────────────────

const INCOME_TYPES = [
  { value: 'salary', label: 'Salary / Wages' },
  { value: 'business', label: 'Business Income' },
  { value: 'investment', label: 'Investment Income' },
  { value: 'rental', label: 'Rental Income' },
  { value: 'other', label: 'Other' },
]

const EXPENSE_TYPES = [
  { value: 'rent_mortgage', label: 'Rent / Mortgage' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'loans', label: 'Loan Repayments' },
  { value: 'credit_cards', label: 'Credit Cards' },
  { value: 'living', label: 'Living Expenses' },
  { value: 'other', label: 'Other' },
]

const LIABILITY_TYPES = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'car_loan', label: 'Car Loan' },
  { value: 'hecs_help', label: 'HECS / HELP' },
  { value: 'mortgage', label: 'Existing Mortgage' },
  { value: 'bnpl', label: 'Buy Now Pay Later' },
  { value: 'other', label: 'Other' },
]

const ASSET_TYPES = [
  { value: 'real_estate', label: 'Real Estate / Property' },
  { value: 'savings', label: 'Savings Account' },
  { value: 'term_deposit', label: 'Term Deposit' },
  { value: 'shares', label: 'Shares / Investments' },
  { value: 'superannuation', label: 'Superannuation' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'other', label: 'Other' },
]

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
]

const FREQ_TO_MONTHLY: Record<string, number> = {
  weekly: 4.333,
  fortnightly: 2.167,
  monthly: 1,
  annual: 1 / 12,
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n)

function capitalize(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

type Tab = 'income' | 'expenses' | 'liabilities' | 'assets'

export default function FinancialProfilePage() {
  const { getAccessToken, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  // ── State ────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true)
  const [incomes, setIncomes] = useState<IncomeEntry[]>([])
  const [expenses, setExpenses] = useState<ExpenseEntry[]>([])
  const [liabilities, setLiabilities] = useState<LiabilityEntry[]>([])
  const [assets, setAssets] = useState<AssetEntry[]>([])
  const [activeTab, setActiveTab] = useState<Tab>('income')

  // Share state
  const [broker, setBroker] = useState<Connection | null>(null)
  const [shared, setShared] = useState(false)
  const [togglingShare, setTogglingShare] = useState(false)

  // Add forms
  const [showAddIncome, setShowAddIncome] = useState(false)
  const [showAddExpense, setShowAddExpense] = useState(false)
  const [showAddLiability, setShowAddLiability] = useState(false)
  const [showAddAsset, setShowAddAsset] = useState(false)

  // Edit state (ID of entry being edited, null = not editing)
  const [editingIncomeId, setEditingIncomeId] = useState<string | null>(null)
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null)
  const [editingLiabilityId, setEditingLiabilityId] = useState<string | null>(null)
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null)

  // Form fields — income
  const [incomeType, setIncomeType] = useState<IncomeType>('salary')
  const [incomeAmount, setIncomeAmount] = useState('')
  const [incomeFrequency, setIncomeFrequency] = useState<Frequency>('monthly')
  const [incomeEmployer, setIncomeEmployer] = useState('')

  // Form fields — expense
  const [expenseType, setExpenseType] = useState<ExpenseType>('living')
  const [expenseAmount, setExpenseAmount] = useState('')
  const [expenseFrequency, setExpenseFrequency] = useState<Frequency>('monthly')
  const [expenseDescription, setExpenseDescription] = useState('')

  // Form fields — liability
  const [liabilityType, setLiabilityType] = useState<LiabilityType>('credit_card')
  const [liabilityLender, setLiabilityLender] = useState('')
  const [liabilityBalance, setLiabilityBalance] = useState('')
  const [liabilityCreditLimit, setLiabilityCreditLimit] = useState('')
  const [liabilityRepayment, setLiabilityRepayment] = useState('')
  const [liabilityRate, setLiabilityRate] = useState('')
  const [liabilityTerm, setLiabilityTerm] = useState('')
  const [liabilityDescription, setLiabilityDescription] = useState('')

  // Form fields — asset
  const [assetType, setAssetType] = useState<AssetType>('savings')
  const [assetValue, setAssetValue] = useState('')
  const [assetDescription, setAssetDescription] = useState('')
  const [assetInstitution, setAssetInstitution] = useState('')
  const [assetAddress, setAssetAddress] = useState('')

  // Submitting state
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // ── Redirect if not authenticated ────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) navigate('/customer')
  }, [isAuthenticated, navigate])

  // ── Load data ────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    const token = getAccessToken()
    if (!token) return
    setLoading(true)
    try {
      const [incomeRes, expenseRes, liabilityRes, assetRes] = await Promise.all([
        getIncomes(token),
        getExpenses(token),
        getLiabilities(token),
        getAssets(token),
      ])
      setIncomes(incomeRes)
      setExpenses(expenseRes)
      setLiabilities(liabilityRes)
      setAssets(assetRes)

      // Load broker connection
      try {
        const connRes = await getMyConnections(token)
        const active = (connRes.connections || []).find(
          (c: Connection) => c.status === 'active',
        )
        if (active) {
          setBroker(active)
          try {
            const shareRes = await getFinancialsSharedStatus(token)
            setShared(shareRes.shared)
          } catch {
            // sharing status not available yet
          }
        }
      } catch {
        // no connections
      }
    } catch {
      setError('Failed to load financial data')
    } finally {
      setLoading(false)
    }
  }, [getAccessToken])

  useEffect(() => {
    loadData()
  }, [loadData])

  // ── Computed values ──────────────────────────────────────────────────────────
  const totalMonthlyIncome = incomes.reduce(
    (sum, i) => sum + i.amount * (FREQ_TO_MONTHLY[i.frequency] || 1),
    0,
  )
  const totalMonthlyExpenses = expenses.reduce(
    (sum, e) => sum + e.amount * (FREQ_TO_MONTHLY[e.frequency] || 1),
    0,
  )
  const totalMonthlyLiabilities = liabilities.reduce(
    (sum, l) => sum + (l.monthly_repayment || 0),
    0,
  )
  const netCashFlow = totalMonthlyIncome - totalMonthlyExpenses - totalMonthlyLiabilities
  const totalAssets = assets.reduce((sum, a) => sum + a.estimated_value, 0)
  const totalLiabilityBalance = liabilities.reduce((sum, l) => sum + l.outstanding_balance, 0)

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleToggleShare = async () => {
    const token = getAccessToken()
    if (!token) return
    setTogglingShare(true)
    try {
      const res = await shareFinancials(token, !shared)
      setShared(res.shared)
    } catch {
      // silent
    } finally {
      setTogglingShare(false)
    }
  }

  const handleAddIncome = async () => {
    const token = getAccessToken()
    if (!token || !incomeAmount) return
    setSubmitting(true)
    setError('')
    try {
      await createIncome(token, {
        income_type: incomeType,
        amount: parseFloat(incomeAmount),
        frequency: incomeFrequency,
        ...(incomeEmployer ? { employer_name: incomeEmployer } : {}),
      })
      setShowAddIncome(false)
      setIncomeAmount('')
      setIncomeEmployer('')
      setIncomeType('salary')
      setIncomeFrequency('monthly')
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add income')
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddExpense = async () => {
    const token = getAccessToken()
    if (!token || !expenseAmount) return
    setSubmitting(true)
    setError('')
    try {
      await createExpense(token, {
        expense_type: expenseType,
        amount: parseFloat(expenseAmount),
        frequency: expenseFrequency,
        ...(expenseDescription ? { description: expenseDescription } : {}),
      })
      setShowAddExpense(false)
      setExpenseAmount('')
      setExpenseDescription('')
      setExpenseType('living')
      setExpenseFrequency('monthly')
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add expense')
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddLiability = async () => {
    const token = getAccessToken()
    if (!token || !liabilityBalance) return
    setSubmitting(true)
    setError('')
    try {
      await createLiability(token, {
        liability_type: liabilityType,
        outstanding_balance: parseFloat(liabilityBalance),
        ...(liabilityLender ? { lender_name: liabilityLender } : {}),
        ...(liabilityCreditLimit && liabilityType === 'credit_card'
          ? { credit_limit: parseFloat(liabilityCreditLimit) }
          : {}),
        ...(liabilityRepayment ? { monthly_repayment: parseFloat(liabilityRepayment) } : {}),
        ...(liabilityRate ? { interest_rate: parseFloat(liabilityRate) } : {}),
        ...(liabilityTerm ? { remaining_term_months: parseInt(liabilityTerm) } : {}),
        ...(liabilityDescription ? { description: liabilityDescription } : {}),
      })
      setShowAddLiability(false)
      setLiabilityBalance('')
      setLiabilityLender('')
      setLiabilityCreditLimit('')
      setLiabilityRepayment('')
      setLiabilityRate('')
      setLiabilityTerm('')
      setLiabilityDescription('')
      setLiabilityType('credit_card')
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add liability')
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddAsset = async () => {
    const token = getAccessToken()
    if (!token || !assetValue) return
    setSubmitting(true)
    setError('')
    try {
      await createAsset(token, {
        asset_type: assetType,
        estimated_value: parseFloat(assetValue),
        ...(assetDescription ? { description: assetDescription } : {}),
        ...(assetInstitution ? { institution_name: assetInstitution } : {}),
        ...(assetAddress && assetType === 'real_estate' ? { address: assetAddress } : {}),
      })
      setShowAddAsset(false)
      setAssetValue('')
      setAssetDescription('')
      setAssetInstitution('')
      setAssetAddress('')
      setAssetType('savings')
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add asset')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteIncome = async (id: string) => {
    const token = getAccessToken()
    if (!token) return
    try {
      await deleteIncome(token, id)
      await loadData()
    } catch {
      // silent
    }
  }

  const handleDeleteExpense = async (id: string) => {
    const token = getAccessToken()
    if (!token) return
    try {
      await deleteExpense(token, id)
      await loadData()
    } catch {
      // silent
    }
  }

  const handleDeleteLiability = async (id: string) => {
    const token = getAccessToken()
    if (!token) return
    try {
      await deleteLiability(token, id)
      await loadData()
    } catch {
      // silent
    }
  }

  const handleDeleteAsset = async (id: string) => {
    const token = getAccessToken()
    if (!token) return
    try {
      await deleteAsset(token, id)
      await loadData()
    } catch {
      // silent
    }
  }

  // ── Edit helpers ────────────────────────────────────────────────────────────
  const startEditIncome = (item: IncomeEntry) => {
    setEditingIncomeId(item.id)
    setShowAddIncome(false)
    setIncomeType(item.income_type)
    setIncomeAmount(String(item.amount))
    setIncomeFrequency(item.frequency)
    setIncomeEmployer(item.employer_name || '')
  }

  const startEditExpense = (item: ExpenseEntry) => {
    setEditingExpenseId(item.id)
    setShowAddExpense(false)
    setExpenseType(item.expense_type)
    setExpenseAmount(String(item.amount))
    setExpenseFrequency(item.frequency)
    setExpenseDescription(item.description || '')
  }

  const startEditLiability = (item: LiabilityEntry) => {
    setEditingLiabilityId(item.id)
    setShowAddLiability(false)
    setLiabilityType(item.liability_type)
    setLiabilityBalance(String(item.outstanding_balance))
    setLiabilityLender(item.lender_name || '')
    setLiabilityCreditLimit(item.credit_limit ? String(item.credit_limit) : '')
    setLiabilityRepayment(item.monthly_repayment ? String(item.monthly_repayment) : '')
    setLiabilityRate(item.interest_rate ? String(item.interest_rate) : '')
    setLiabilityTerm(item.remaining_term_months ? String(item.remaining_term_months) : '')
    setLiabilityDescription(item.description || '')
  }

  const startEditAsset = (item: AssetEntry) => {
    setEditingAssetId(item.id)
    setShowAddAsset(false)
    setAssetType(item.asset_type)
    setAssetValue(String(item.estimated_value))
    setAssetDescription(item.description || '')
    setAssetInstitution(item.institution_name || '')
    setAssetAddress(item.address || '')
  }

  const cancelEdit = () => {
    setEditingIncomeId(null)
    setEditingExpenseId(null)
    setEditingLiabilityId(null)
    setEditingAssetId(null)
  }

  const handleUpdateIncome = async () => {
    const token = getAccessToken()
    if (!token || !editingIncomeId || !incomeAmount) return
    setSubmitting(true)
    setError('')
    try {
      await updateIncome(token, editingIncomeId, {
        income_type: incomeType,
        amount: parseFloat(incomeAmount),
        frequency: incomeFrequency,
        employer_name: incomeEmployer || undefined,
      })
      setEditingIncomeId(null)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update income')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdateExpense = async () => {
    const token = getAccessToken()
    if (!token || !editingExpenseId || !expenseAmount) return
    setSubmitting(true)
    setError('')
    try {
      await updateExpense(token, editingExpenseId, {
        expense_type: expenseType,
        amount: parseFloat(expenseAmount),
        frequency: expenseFrequency,
        description: expenseDescription || undefined,
      })
      setEditingExpenseId(null)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update expense')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdateLiability = async () => {
    const token = getAccessToken()
    if (!token || !editingLiabilityId || !liabilityBalance) return
    setSubmitting(true)
    setError('')
    try {
      await updateLiability(token, editingLiabilityId, {
        liability_type: liabilityType,
        outstanding_balance: parseFloat(liabilityBalance),
        lender_name: liabilityLender || undefined,
        credit_limit: liabilityCreditLimit && liabilityType === 'credit_card' ? parseFloat(liabilityCreditLimit) : undefined,
        monthly_repayment: liabilityRepayment ? parseFloat(liabilityRepayment) : undefined,
        interest_rate: liabilityRate ? parseFloat(liabilityRate) : undefined,
        remaining_term_months: liabilityTerm ? parseInt(liabilityTerm) : undefined,
        description: liabilityDescription || undefined,
      })
      setEditingLiabilityId(null)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update liability')
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdateAsset = async () => {
    const token = getAccessToken()
    if (!token || !editingAssetId || !assetValue) return
    setSubmitting(true)
    setError('')
    try {
      await updateAsset(token, editingAssetId, {
        asset_type: assetType,
        estimated_value: parseFloat(assetValue),
        description: assetDescription || undefined,
        institution_name: assetInstitution || undefined,
        address: assetAddress && assetType === 'real_estate' ? assetAddress : undefined,
      })
      setEditingAssetId(null)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update asset')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <div className="max-w-5xl mx-auto px-4 py-12 text-center text-gray-500">
          Loading financial profile...
        </div>
      </div>
    )
  }

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'income', label: 'Income', count: incomes.length },
    { key: 'expenses', label: 'Expenses', count: expenses.length },
    { key: 'liabilities', label: 'Liabilities', count: liabilities.length },
    { key: 'assets', label: 'Assets', count: assets.length },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Back link */}
        <button
          onClick={() => navigate('/customer')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-6">Financial Profile</h1>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
            {error}
            <button onClick={() => setError('')}>
              <X size={16} />
            </button>
          </div>
        )}

        {/* ── Summary Cards ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-green-600" />
              <span className="text-xs text-gray-500">Monthly Income</span>
            </div>
            <p className="text-lg font-bold text-green-700">{fmt(totalMonthlyIncome)}</p>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown size={16} className="text-red-600" />
              <span className="text-xs text-gray-500">Monthly Expenses</span>
            </div>
            <p className="text-lg font-bold text-red-700">{fmt(totalMonthlyExpenses)}</p>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield size={16} className="text-amber-600" />
              <span className="text-xs text-gray-500">Monthly Liabilities</span>
            </div>
            <p className="text-lg font-bold text-amber-700">{fmt(totalMonthlyLiabilities)}</p>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign size={16} className={netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'} />
              <span className="text-xs text-gray-500">Net Cash Flow</span>
            </div>
            <p className={`text-lg font-bold ${netCashFlow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {fmt(netCashFlow)}
            </p>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 mb-1">
              <PiggyBank size={16} className="text-blue-600" />
              <span className="text-xs text-gray-500">Total Assets</span>
            </div>
            <p className="text-lg font-bold text-blue-700">{fmt(totalAssets)}</p>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Landmark size={16} className="text-amber-600" />
              <span className="text-xs text-gray-500">Total Liabilities</span>
            </div>
            <p className="text-lg font-bold text-amber-700">{fmt(totalLiabilityBalance)}</p>
          </div>
        </div>

        {/* ── Share Banner ─────────────────────────────────────────────────────── */}
        {broker && (
          <div
            className={`rounded-xl p-4 mb-6 flex items-center justify-between gap-4 ${
              shared
                ? 'bg-green-50 border border-green-200'
                : 'bg-amber-50 border border-amber-200'
            }`}
          >
            <div className="flex items-center gap-3">
              {shared ? (
                <ShieldCheck size={20} className="text-green-600 flex-shrink-0" />
              ) : (
                <Lock size={20} className="text-amber-600 flex-shrink-0" />
              )}
              <p className={`text-sm ${shared ? 'text-green-800' : 'text-amber-800'}`}>
                {shared
                  ? `Your financial profile is shared with ${broker.broker_name || 'your broker'}`
                  : `Share your financial profile with ${broker.broker_name || 'your broker'} to support your mortgage application`}
              </p>
            </div>
            <button
              onClick={handleToggleShare}
              disabled={togglingShare}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex-shrink-0 transition-colors disabled:opacity-50 ${
                shared
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {togglingShare ? '...' : shared ? 'Shared' : 'Share Now'}
            </button>
          </div>
        )}

        {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
        <div className="flex gap-1 border-b mb-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              <span
                className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full ${
                  activeTab === tab.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* ── Income Tab ───────────────────────────────────────────────────────── */}
        {activeTab === 'income' && (
          <div className="space-y-3">
            {incomes.length === 0 && !showAddIncome && (
              <p className="text-sm text-gray-500 text-center py-8">
                No income entries yet. Add your first income source below.
              </p>
            )}

            {incomes.map(item => (
              <div
                key={item.id}
                className={`bg-green-50 border rounded-xl px-4 py-3 flex items-center justify-between ${
                  editingIncomeId === item.id ? 'border-blue-400 ring-2 ring-blue-200' : 'border-green-200'
                }`}
              >
                <div>
                  <p className="text-sm font-medium text-green-900">
                    {capitalize(item.income_type)}
                    {item.employer_name && (
                      <span className="text-green-700 font-normal"> ({item.employer_name})</span>
                    )}
                  </p>
                  <p className="text-xs text-green-700 mt-0.5">
                    {fmt(item.amount)} / {item.frequency}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEditIncome(item)}
                    className="text-green-400 hover:text-blue-500 transition-colors p-1"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteIncome(item.id)}
                    className="text-green-400 hover:text-red-500 transition-colors p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}

            {(showAddIncome || editingIncomeId) ? (
              <div className={`bg-white border rounded-xl p-4 space-y-3 ${editingIncomeId ? 'border-blue-300' : 'border-green-200'}`}>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">{editingIncomeId ? 'Edit Income' : 'Add Income'}</h3>
                  <button onClick={() => { setShowAddIncome(false); cancelEdit() }} className="text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={incomeType}
                    onChange={e => setIncomeType(e.target.value as IncomeType)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {INCOME_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Amount ($)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatNumberWithCommas(incomeAmount)}
                      onChange={e => setIncomeAmount(stripToNumber(e.target.value))}
                      placeholder="0.00"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
                    <select
                      value={incomeFrequency}
                      onChange={e => setIncomeFrequency(e.target.value as Frequency)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      {FREQUENCIES.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Employer Name <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={incomeEmployer}
                    onChange={e => setIncomeEmployer(e.target.value)}
                    placeholder="e.g. Acme Corp"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <button
                  onClick={editingIncomeId ? handleUpdateIncome : handleAddIncome}
                  disabled={submitting || !incomeAmount}
                  className={`w-full text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors ${
                    editingIncomeId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {submitting ? 'Saving...' : editingIncomeId ? 'Save Changes' : 'Add Income'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setShowAddIncome(true); cancelEdit() }}
                className="w-full border-2 border-dashed border-green-300 text-green-600 rounded-xl py-3 text-sm font-medium hover:bg-green-50 hover:border-green-400 transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                Add Income
              </button>
            )}
          </div>
        )}

        {/* ── Expenses Tab ─────────────────────────────────────────────────────── */}
        {activeTab === 'expenses' && (
          <div className="space-y-3">
            {expenses.length === 0 && !showAddExpense && (
              <p className="text-sm text-gray-500 text-center py-8">
                No expense entries yet. Add your first expense below.
              </p>
            )}

            {expenses.map(item => (
              <div
                key={item.id}
                className={`bg-red-50 border rounded-xl px-4 py-3 flex items-center justify-between ${
                  editingExpenseId === item.id ? 'border-blue-400 ring-2 ring-blue-200' : 'border-red-200'
                }`}
              >
                <div>
                  <p className="text-sm font-medium text-red-900">
                    {capitalize(item.expense_type)}
                    {item.description && (
                      <span className="text-red-700 font-normal"> ({item.description})</span>
                    )}
                  </p>
                  <p className="text-xs text-red-700 mt-0.5">
                    {fmt(item.amount)} / {item.frequency}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEditExpense(item)}
                    className="text-red-400 hover:text-blue-500 transition-colors p-1"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteExpense(item.id)}
                    className="text-red-400 hover:text-red-600 transition-colors p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}

            {(showAddExpense || editingExpenseId) ? (
              <div className={`bg-white border rounded-xl p-4 space-y-3 ${editingExpenseId ? 'border-blue-300' : 'border-red-200'}`}>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">{editingExpenseId ? 'Edit Expense' : 'Add Expense'}</h3>
                  <button onClick={() => { setShowAddExpense(false); cancelEdit() }} className="text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={expenseType}
                    onChange={e => setExpenseType(e.target.value as ExpenseType)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    {EXPENSE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Amount ($)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatNumberWithCommas(expenseAmount)}
                      onChange={e => setExpenseAmount(stripToNumber(e.target.value))}
                      placeholder="0.00"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
                    <select
                      value={expenseFrequency}
                      onChange={e => setExpenseFrequency(e.target.value as Frequency)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      {FREQUENCIES.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={expenseDescription}
                    onChange={e => setExpenseDescription(e.target.value)}
                    placeholder="e.g. Electricity & Gas"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <button
                  onClick={editingExpenseId ? handleUpdateExpense : handleAddExpense}
                  disabled={submitting || !expenseAmount}
                  className={`w-full text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors ${
                    editingExpenseId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {submitting ? 'Saving...' : editingExpenseId ? 'Save Changes' : 'Add Expense'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setShowAddExpense(true); cancelEdit() }}
                className="w-full border-2 border-dashed border-red-300 text-red-600 rounded-xl py-3 text-sm font-medium hover:bg-red-50 hover:border-red-400 transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                Add Expense
              </button>
            )}
          </div>
        )}

        {/* ── Liabilities Tab ──────────────────────────────────────────────────── */}
        {activeTab === 'liabilities' && (
          <div className="space-y-3">
            {liabilities.length === 0 && !showAddLiability && (
              <p className="text-sm text-gray-500 text-center py-8">
                No liabilities recorded. Add any debts or ongoing repayments below.
              </p>
            )}

            {liabilities.map(item => (
              <div
                key={item.id}
                className={`bg-amber-50 border rounded-xl px-4 py-3 flex items-center justify-between ${editingLiabilityId === item.id ? 'border-amber-400 ring-2 ring-amber-300' : 'border-amber-200'}`}
              >
                <div>
                  <p className="text-sm font-medium text-amber-900">
                    {capitalize(item.liability_type)}
                    {item.lender_name && (
                      <span className="text-amber-700 font-normal"> ({item.lender_name})</span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-3 mt-1">
                    <span className="text-xs text-amber-700">
                      Owing: {fmt(item.outstanding_balance)}
                    </span>
                    {item.monthly_repayment != null && item.monthly_repayment > 0 && (
                      <span className="text-xs text-amber-700">
                        Repayment: {fmt(item.monthly_repayment)}/mo
                      </span>
                    )}
                    {item.interest_rate != null && item.interest_rate > 0 && (
                      <span className="text-xs text-amber-700">
                        Rate: {item.interest_rate}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEditLiability(item)}
                    className="text-amber-400 hover:text-blue-500 transition-colors p-1"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteLiability(item.id)}
                    className="text-amber-400 hover:text-red-500 transition-colors p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}

            {(showAddLiability || editingLiabilityId) ? (
              <div className="bg-white border border-amber-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">{editingLiabilityId ? 'Edit Liability' : 'Add Liability'}</h3>
                  <button onClick={() => { setShowAddLiability(false); cancelEdit() }} className="text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={liabilityType}
                    onChange={e => setLiabilityType(e.target.value as LiabilityType)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    {LIABILITY_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Lender Name <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={liabilityLender}
                    onChange={e => setLiabilityLender(e.target.value)}
                    placeholder="e.g. ANZ, Afterpay"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Outstanding Balance ($) *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatNumberWithCommas(liabilityBalance)}
                      onChange={e => setLiabilityBalance(stripToNumber(e.target.value))}
                      placeholder="0.00"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  {liabilityType === 'credit_card' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Credit Limit ($) <span className="text-gray-400">(opt)</span>
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formatNumberWithCommas(liabilityCreditLimit)}
                        onChange={e => setLiabilityCreditLimit(stripToNumber(e.target.value))}
                        placeholder="0.00"
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Monthly Repayment <span className="text-gray-400">(opt)</span>
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatNumberWithCommas(liabilityRepayment)}
                      onChange={e => setLiabilityRepayment(stripToNumber(e.target.value))}
                      placeholder="0.00"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Interest Rate % <span className="text-gray-400">(opt)</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={liabilityRate}
                      onChange={e => setLiabilityRate(e.target.value)}
                      placeholder="0.00"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Term (months) <span className="text-gray-400">(opt)</span>
                    </label>
                    <input
                      type="number"
                      value={liabilityTerm}
                      onChange={e => setLiabilityTerm(e.target.value)}
                      placeholder="0"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={liabilityDescription}
                    onChange={e => setLiabilityDescription(e.target.value)}
                    placeholder="e.g. Car finance for Toyota Corolla"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                <button
                  onClick={editingLiabilityId ? handleUpdateLiability : handleAddLiability}
                  disabled={submitting || !liabilityBalance}
                  className={`w-full text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors ${editingLiabilityId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                >
                  {submitting ? 'Saving...' : editingLiabilityId ? 'Save Changes' : 'Add Liability'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setShowAddLiability(true); cancelEdit() }}
                className="w-full border-2 border-dashed border-amber-300 text-amber-600 rounded-xl py-3 text-sm font-medium hover:bg-amber-50 hover:border-amber-400 transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                Add Liability
              </button>
            )}
          </div>
        )}

        {/* ── Assets Tab ───────────────────────────────────────────────────────── */}
        {activeTab === 'assets' && (
          <div className="space-y-3">
            {assets.length === 0 && !showAddAsset && (
              <p className="text-sm text-gray-500 text-center py-8">
                No assets recorded. Add your properties, savings, and investments below.
              </p>
            )}

            {assets.map(item => (
              <div
                key={item.id}
                className={`bg-blue-50 border rounded-xl px-4 py-3 flex items-center justify-between ${editingAssetId === item.id ? 'border-blue-400 ring-2 ring-blue-300' : 'border-blue-200'}`}
              >
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    {capitalize(item.asset_type)}
                    {item.institution_name && (
                      <span className="text-blue-700 font-normal"> ({item.institution_name})</span>
                    )}
                  </p>
                  {item.asset_type === 'real_estate' && item.address && (
                    <p className="text-xs text-blue-600 mt-0.5">{item.address}</p>
                  )}
                  <p className="text-xs text-blue-700 mt-0.5">
                    Value: {fmt(item.estimated_value)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEditAsset(item)}
                    className="text-blue-400 hover:text-blue-600 transition-colors p-1"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteAsset(item.id)}
                    className="text-blue-400 hover:text-red-500 transition-colors p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}

            {(showAddAsset || editingAssetId) ? (
              <div className="bg-white border border-blue-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">{editingAssetId ? 'Edit Asset' : 'Add Asset'}</h3>
                  <button onClick={() => { setShowAddAsset(false); cancelEdit() }} className="text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={assetType}
                    onChange={e => setAssetType(e.target.value as AssetType)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {ASSET_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Estimated Value ($) *</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={formatNumberWithCommas(assetValue)}
                    onChange={e => setAssetValue(stripToNumber(e.target.value))}
                    placeholder="0.00"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={assetDescription}
                    onChange={e => setAssetDescription(e.target.value)}
                    placeholder="e.g. Emergency fund"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Institution Name <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={assetInstitution}
                    onChange={e => setAssetInstitution(e.target.value)}
                    placeholder="e.g. Commonwealth Bank"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {assetType === 'real_estate' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Property Address <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={assetAddress}
                      onChange={e => setAssetAddress(e.target.value)}
                      placeholder="e.g. 42 Wallaby Way, Sydney NSW 2000"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}

                <button
                  onClick={editingAssetId ? handleUpdateAsset : handleAddAsset}
                  disabled={submitting || !assetValue}
                  className={`w-full text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors ${editingAssetId ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {submitting ? 'Saving...' : editingAssetId ? 'Save Changes' : 'Add Asset'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setShowAddAsset(true); cancelEdit() }}
                className="w-full border-2 border-dashed border-blue-300 text-blue-600 rounded-xl py-3 text-sm font-medium hover:bg-blue-50 hover:border-blue-400 transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                Add Asset
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
